import { useEffect } from "react";

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export function SEOHead({
  title = "Lej privat parkering i Danmark | SharePark",
  description = "Find og book private parkeringspladser i Danmark. Søg efter ledig parkering, se priser og udlej din egen plads sikkert med SharePark.",
  keywords = "privat parkering, lej parkeringsplads, udlej parkeringsplads, ledig parkering, billig parkering, parkering danmark, SharePark",
  image = "https://sharepark.dk/og-image.jpg",
  url,
  type = "website",
  jsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper function to set meta tag
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute("content", content);
    };

    // Basic meta tags
    setMetaTag("description", description);
    setMetaTag("keywords", keywords);

    // Open Graph tags
    setMetaTag("og:title", title, true);
    setMetaTag("og:description", description, true);
    setMetaTag("og:type", type, true);
    setMetaTag("og:image", image, true);
    if (url) setMetaTag("og:url", url, true);

    // Twitter Card tags
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);
    setMetaTag("twitter:image", image);

    // Canonical URL
    if (url) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement("link");
        linkElement.setAttribute("rel", "canonical");
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute("href", url);
    }

    let structuredData = document.querySelector<HTMLScriptElement>('script[data-sharepark-json-ld="true"]');
    if (jsonLd) {
      if (!structuredData) {
        structuredData = document.createElement("script");
        structuredData.type = "application/ld+json";
        structuredData.dataset.shareparkJsonLd = "true";
        document.head.appendChild(structuredData);
      }
      structuredData.textContent = JSON.stringify(jsonLd);
    } else if (structuredData) {
      structuredData.remove();
    }
  }, [title, description, keywords, image, url, type, jsonLd]);

  return null;
}
